// Team › Loops (`/c/{slug}/agents/loops`; LOOP-BREAKER, founder ask 2026-10-30): the runs the loop
// breaker stopped, newest first, with what each burned (tokens, cost, minutes) and the plain
// reason. An open one offers Retry (with a hint the DE gets on its next wake-up), Reassign (the
// task goes to another seat: `tasks.assign` with a handoff, then `resolve` reassign) and Close,
// each a modal. Managers also get Settings: the company's override of the thresholds, each field
// showing the platform default. Labels are short; explanations sit behind (i).
// Calls on load: `loopIncidents.list` (the Open / All filter as `?status`); a manager's Settings
// reads `loopBreaker.settings.get` when opened.
import type { OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  ListRow,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Switch,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowRightLeftIcon,
  CheckIcon,
  RepeatIcon,
  RotateCcwIcon,
  SettingsIcon,
} from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isManager } from "../company";
import { problemText } from "../de/de-status";
import { formatDateTime } from "../format";
import { seatsQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { moveTask, useAllPages } from "./activity-screen";
import { formatMicros, formatTokens, useCostCurrency } from "./agent-parts";
import { invalidateAgents } from "./agent-queries";
import { LIST_BODY, RowAction } from "./agents-ux";

export type LoopIncident = OperationOutput<"loopIncidents.list">["data"][number];
type Settings = OperationOutput<"loopBreaker.settings.get">;
type Thresholds = Settings["platform"];

export const LOOP_FILTERS = ["open", "all"] as const;
export type LoopFilter = (typeof LOOP_FILTERS)[number];

export const loopsKey = (co: string) => ["company", co, "loop-incidents"] as const;
const settingsKey = (co: string) => ["company", co, "loop-breaker-settings"] as const;

const STATUS: Record<
  LoopIncident["status"],
  [string, "neutral" | "info" | "success" | "warning" | "danger"]
> = {
  open: ["Open", "warning"],
  retried: ["Retried", "info"],
  reassigned: ["Reassigned", "neutral"],
  closed: ["Closed", "neutral"],
};

export function LoopsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const manager = isManager(company);
  const [filter, setFilter] = useState<LoopFilter>("open");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const currency = useCostCurrency(api, co);
  const q = useQuery({
    queryKey: [...loopsKey(co), filter],
    queryFn: () =>
      api.client.loopIncidents.list({
        params: { co },
        query: { limit: 200, ...(filter === "open" ? { status: "open" as const } : {}) },
      }),
  });
  const totals = q.data?.totals;
  const rows = q.data?.data ?? [];
  return (
    <div className={LIST_BODY} data-slot="loops">
      <LocationHeader
        icon={RepeatIcon}
        title="Loops"
        badge={
          <InfoTip label="About loops">
            A run is stopped when it repeats the same calls, or the same intent, without progress,
            or spends tokens with no progress for too long. It gets one warning first.
          </InfoTip>
        }
        actions={
          manager ? (
            <Button variant="ghost" size="sm" onClick={() => setSettingsOpen(true)}>
              <SettingsIcon aria-hidden="true" />
              Settings
            </Button>
          ) : null
        }
      />
      <Toolbar bleed aria-label="Loop filters">
        <ToggleGroup
          variant="chip"
          value={[filter]}
          onValueChange={(v: unknown[]) => {
            const next = v[0] as LoopFilter | undefined;
            if (next) setFilter(next);
          }}
          aria-label="Show"
        >
          <ToggleGroupItem value="open">Open</ToggleGroupItem>
          <ToggleGroupItem value="all">All</ToggleGroupItem>
        </ToggleGroup>
        {totals ? (
          <span
            className="ml-auto flex shrink-0 items-center gap-3 text-xs text-muted-foreground tabular-nums"
            data-slot="loops-totals"
          >
            <span>
              <span className="font-medium text-foreground">{totals.open}</span> open
            </span>
            <span>
              <span className="font-medium text-foreground">{formatTokens(totals.tokens)}</span>{" "}
              tokens burned
            </span>
            <span>
              <span className="font-medium text-foreground">
                {formatMicros(totals.cost_micros, currency)}
              </span>{" "}
              burned
            </span>
          </span>
        ) : null}
      </Toolbar>
      <div className="flex min-w-0 flex-col px-6 pt-3 max-md:px-4">
        {q.isPending ? (
          <Loading label="Loading loops" rows={4} />
        ) : q.isError ? (
          <LoadError what="Loops" error={q.error} />
        ) : !rows.length ? (
          <EmptyState variant="card" icon={RepeatIcon} title="No loops caught" />
        ) : (
          <ul aria-label="Loops" data-slot="loops-list">
            {rows.map((x) => (
              <LoopRow key={x.id} api={api} company={company} x={x} currency={currency} />
            ))}
          </ul>
        )}
      </div>
      {settingsOpen ? (
        <LoopSettingsDialog api={api} company={company} onClose={() => setSettingsOpen(false)} />
      ) : null}
    </div>
  );
}

function LoopRow({
  api,
  company,
  x,
  currency,
}: {
  api: Api;
  company: CompanyAccess;
  x: LoopIncident;
  currency: string | undefined;
}) {
  const [label, tone] = STATUS[x.status];
  const name = workerName(x.agent_name);
  const open = x.status === "open";
  const [dialog, setDialog] = useState<"retry" | "reassign" | "close" | null>(null);
  return (
    <li data-incident={x.id} data-status={x.status}>
      <ListRow
        title={
          <span className="inline-flex min-w-0 items-center gap-2">
            <WorkerBadge kind="de" size="sm" />
            <span className="shrink-0 font-medium">{name}</span>
            {x.task_id && x.task_title ? (
              <Link
                to="/c/$slug/work/t/$taskId"
                params={{ slug: company.slug, taskId: x.task_id }}
                className="max-w-[32ch] shrink-0 truncate hover:underline"
                title={x.task_title}
                data-slot="loop-task"
              >
                {x.task_title}
              </Link>
            ) : x.task_title ? (
              <span className="max-w-[32ch] shrink-0 truncate" title={x.task_title}>
                {x.task_title}
              </span>
            ) : null}
            <span
              className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground"
              title={x.reason}
              data-slot="loop-reason"
            >
              {x.reason}
            </span>
          </span>
        }
        trailing={
          <span className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground tabular-nums">
            <span title="Tokens burned">{formatTokens(x.tokens_in + x.tokens_out)} tok</span>
            <span title="Cost burned">{formatMicros(x.cost_micros, currency)}</span>
            <span title="Minutes">{x.minutes} min</span>
            <span className="w-28 text-right">{formatDateTime(x.detected_at)}</span>
            <Badge variant={tone}>{label}</Badge>
          </span>
        }
        actions={
          open ? (
            <>
              <RowAction
                icon={RotateCcwIcon}
                label={`Retry ${name}`}
                hint="Wake it again with a hint"
                onClick={() => setDialog("retry")}
              />
              {x.task_id && x.seat_id ? (
                <RowAction
                  icon={ArrowRightLeftIcon}
                  label="Reassign task"
                  onClick={() => setDialog("reassign")}
                />
              ) : null}
              <RowAction icon={CheckIcon} label="Close" onClick={() => setDialog("close")} />
            </>
          ) : null
        }
      />
      {dialog === "retry" ? (
        <RetryDialog api={api} company={company} x={x} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === "reassign" ? (
        <ReassignDialog api={api} company={company} x={x} onClose={() => setDialog(null)} />
      ) : null}
      <CloseDialog
        api={api}
        company={company}
        x={x}
        open={dialog === "close"}
        onClose={() => setDialog(null)}
      />
    </li>
  );
}

function useResolve(api: Api, company: CompanyAccess, x: LoopIncident) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      action: "retry" | "reassign" | "close";
      hint?: string;
      seat_id?: string;
    }) => api.client.loopIncidents.resolve({ params: { co: company.id, id: x.id }, body }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: loopsKey(company.id) });
      void invalidateAgents(qc, company.id);
    },
  });
}

function RetryDialog({
  api,
  company,
  x,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  x: LoopIncident;
  onClose: () => void;
}) {
  const id = useId();
  const [hint, setHint] = useState("");
  const resolve = useResolve(api, company, x);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const h = hint.trim();
    if (!h) return;
    resolve.mutate(
      { action: "retry", hint: h },
      {
        onSuccess: () => {
          toast.add({ title: `${workerName(x.agent_name)} will retry` });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Retry {workerName(x.agent_name)}</DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-hint`}>Hint</FieldLabel>
            <Textarea
              id={`${id}-hint`}
              value={hint}
              required
              maxLength={2000}
              placeholder="What to do differently"
              onChange={(e) => setHint(e.target.value)}
            />
          </Field>
          {resolve.isError ? (
            <p role="alert" className="text-sm text-danger-text">
              {problemText(resolve.error, "The retry did not start.")}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!hint.trim() || resolve.isPending}>
              {resolve.isPending ? "Retrying…" : "Retry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReassignDialog({
  api,
  company,
  x,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  x: LoopIncident;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const seats = useAllPages(useInfiniteQuery(seatsQuery(api, company.id)));
  const choices = seats
    .filter((s) => s.id !== x.seat_id && s.status !== "closed")
    .sort((a, b) => a.title.localeCompare(b.title));
  const [seat, setSeat] = useState("");
  const [note, setNote] = useState("");
  const resolve = useResolve(api, company, x);
  const assign = useMutation({
    mutationFn: async () => {
      if (!x.task_id || !x.seat_id) throw new Error("This loop has no task to reassign.");
      await moveTask(api, company.id, x.task_id, x.seat_id, seat, note.trim() || x.reason);
      await resolve.mutateAsync({ action: "reassign", seat_id: seat });
    },
    onSuccess: () => {
      toast.add({ title: "Task reassigned" });
      void qc.invalidateQueries({ queryKey: ["company", company.id, "work"] });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (seat) assign.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Reassign “{x.task_title ?? "task"}”</DialogTitle>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-seat`}>To</FieldLabel>
            <NativeSelect
              id={`${id}-seat`}
              value={seat}
              onChange={(e) => setSeat(e.target.value)}
              required
            >
              <NativeSelectOption value="">Choose a seat</NativeSelectOption>
              {choices.map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.title}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-note`}>Note</FieldLabel>
            <Textarea
              id={`${id}-note`}
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          {assign.isError ? (
            <p role="alert" className="text-sm text-danger-text">
              {problemText(assign.error, "The task was not reassigned.")}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!seat || assign.isPending}>
              {assign.isPending ? "Reassigning…" : "Reassign"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CloseDialog({
  api,
  company,
  x,
  open,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  x: LoopIncident;
  open: boolean;
  onClose: () => void;
}) {
  const resolve = useResolve(api, company, x);
  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogTitle>Close this loop?</AlertDialogTitle>
          <AlertDialogDescription>Nothing runs again.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              resolve.mutate(
                { action: "close" },
                {
                  onError: (e) =>
                    toast.add({ title: problemText(e, "The loop was not closed."), type: "error" }),
                },
              );
              onClose();
            }}
          >
            Close loop
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

type NumberKey = Exclude<keyof Thresholds, "enabled">;
const NUMBER_FIELDS: { key: NumberKey; label: string; min: number; max: number; info: string }[] = [
  {
    key: "repeat_limit",
    label: "Repeat limit",
    min: 2,
    max: 20,
    info: "Stop after the same cycle of calls repeats this many times without progress.",
  },
  {
    key: "intent_repeat_limit",
    label: "Intent repeat limit",
    min: 2,
    max: 50,
    info: "Stop after the agent states the same intent this many times over read-only calls.",
  },
  {
    key: "stall_minutes",
    label: "Stall minutes",
    min: 1,
    max: 240,
    info: "Stop after this many minutes of spending tokens with no progress.",
  },
  {
    key: "similarity_pct",
    label: "Similarity %",
    min: 50,
    max: 100,
    info: "How alike two intents must be to count as the same.",
  },
];

/** The company's override (empty: the platform default). */
type Draft = { enabled: boolean | null } & Record<NumberKey, string>;

function draftOf(s: Settings): Draft {
  const o = s.override ?? {};
  const num = (k: NumberKey) => (o[k] === null || o[k] === undefined ? "" : String(o[k]));
  return {
    enabled: o.enabled ?? null,
    repeat_limit: num("repeat_limit"),
    intent_repeat_limit: num("intent_repeat_limit"),
    stall_minutes: num("stall_minutes"),
    similarity_pct: num("similarity_pct"),
  };
}

export function LoopSettingsDialog({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: settingsKey(company.id),
    queryFn: () => api.client.loopBreaker.settings.get({ params: { co: company.id } }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Loop breaker</DialogTitle>
        </DialogHeader>
        {q.isPending ? (
          <Loading label="Loading the settings" rows={3} />
        ) : q.isError ? (
          <LoadError what="Settings" error={q.error} />
        ) : (
          <SettingsForm api={api} company={company} settings={q.data} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SettingsForm({
  api,
  company,
  settings,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  settings: Settings;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => draftOf(settings));
  const p = settings.platform;
  const save = useMutation({
    mutationFn: () => {
      const num = (k: NumberKey) => (draft[k].trim() === "" ? null : Number(draft[k]));
      return api.client.loopBreaker.settings.update({
        params: { co: company.id },
        body: {
          enabled: draft.enabled,
          repeat_limit: num("repeat_limit"),
          intent_repeat_limit: num("intent_repeat_limit"),
          stall_minutes: num("stall_minutes"),
          similarity_pct: num("similarity_pct"),
        },
      });
    },
    onSuccess: (next) => {
      qc.setQueryData(settingsKey(company.id), next);
      toast.add({ title: "Loop settings saved" });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const enabled = draft.enabled ?? p.enabled;
  return (
    <form onSubmit={submit} className="flex flex-col gap-3" data-slot="loop-settings">
      <SettingRow
        label="On"
        htmlFor={`${id}-enabled`}
        info={`Catch and stop loops in this company. Default: ${p.enabled ? "on" : "off"}.`}
        overridden={draft.enabled !== null}
        onReset={() => setDraft((d) => ({ ...d, enabled: null }))}
      >
        <Switch
          id={`${id}-enabled`}
          aria-label="On"
          checked={enabled}
          onCheckedChange={(v: boolean) => setDraft((d) => ({ ...d, enabled: v }))}
        />
      </SettingRow>
      {NUMBER_FIELDS.map((f) => (
        <SettingRow
          key={f.key}
          label={f.label}
          htmlFor={`${id}-${f.key}`}
          info={`${f.info} Default: ${p[f.key]}.`}
          overridden={draft[f.key].trim() !== ""}
          onReset={() => setDraft((d) => ({ ...d, [f.key]: "" }))}
        >
          <Input
            id={`${id}-${f.key}`}
            type="number"
            inputMode="numeric"
            min={f.min}
            max={f.max}
            step={1}
            className="w-24"
            placeholder={String(p[f.key])}
            value={draft[f.key]}
            onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
          />
        </SettingRow>
      ))}
      {save.isError ? (
        <p role="alert" className="text-sm text-danger-text">
          {problemText(save.error, "The settings were not saved.")}
        </p>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function SettingRow({
  label,
  htmlFor,
  info,
  overridden,
  onReset,
  children,
}: {
  label: string;
  htmlFor: string;
  info: string;
  overridden: boolean;
  onReset: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-9 items-center gap-2" data-setting={htmlFor}>
      <label htmlFor={htmlFor} className="text-sm">
        {label}
      </label>
      <InfoTip label={`About ${label.toLowerCase()}`}>{info}</InfoTip>
      <span className="ml-auto flex items-center gap-2">
        {overridden ? (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={onReset}
            aria-label={`Use default ${label.toLowerCase()}`}
          >
            Use default
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">Default</span>
        )}
        {children}
      </span>
    </div>
  );
}
